import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ResponseCode } from '@lsq-frontend/angular-shared/api-service';
import {
    AuthenticationService,
    Color,
    focusOnFirstInvalidLsqInput,
    Icons,
    ModalWorkflowService
} from '@lsq-frontend/common-ui';
import {
    CreateTotpMfaTokenRequest,
    VerifyTotpMfaTokenRequest
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    CreateTotpMfaTokenService,
    VerifyTotpMfaTokenService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { TwoFactorService } from '../two-factor.service';
import { first } from 'rxjs/operators';

@Component({
    selector: 'edit-totp-modal',
    template: `
        <form class="modal-content" [formGroup]="totpGroup" (ngSubmit)="submitTOTPCode()">
            <div class="modal-header" [style.padding-top]="'3rem'">
                <h2 i18n>Enroll New Device</h2>
                <lsq-button
                    appearance="transparent"
                    class="close"
                    ariaLabel="Close"
                    i18n-ariaLabel
                    (buttonClick)="modalWorkflowService.hideWorkflow()"
                >
                    <lsq-icon aria-hidden="true" [type]="icons.Close" [color]="colors.Black">
                    </lsq-icon>
                </lsq-button>
            </div>
            <div class="modal-body">
                <div *ngIf="modalLoading">
                    <lsq-circle-loader></lsq-circle-loader>
                </div>
                <div *ngIf="!modalLoading">
                    <p class="body" [style.marginBottom]="'2rem'">
                        To enroll your device in multi-factor enrollment, scan the QR code and enter
                        the verification code provided by Duo Mobile.
                    </p>
                    <div class="qrcode-image">
                        <qrcode
                            [qrdata]="this.qrCode"
                            [cssClass]="'center'"
                            [colorDark]="'#000000ff'"
                            [colorLight]="'#ffffffff'"
                            [elementType]="'canvas'"
                            [errorCorrectionLevel]="'M'"
                            [imageHeight]="75"
                            [imageWidth]="75"
                            [margin]="4"
                            [scale]="1"
                            [width]="200"
                            [allowEmptyString]="true"
                        ></qrcode>
                    </div>
                    <p class="label" [style.marginBottom]="'-0.5rem'">Enter Verification Code</p>
                    <lsq-input
                        [style.marginBottom]="'1rem'"
                        name="totpCode"
                        [basicLabel]="false"
                        formControlName="totpCode"
                        [maxLength]="6"
                        [autoFocus]="true"
                        [group]="totpGroup"
                    >
                    </lsq-input>
                </div>
            </div>
            <div class="modal-footer">
                <lsq-button
                    i18n
                    size="sm"
                    type="button"
                    appearance="secondary"
                    (buttonClick)="modalWorkflowService.hideWorkflow()"
                >
                    Cancel
                </lsq-button>

                <lsq-button
                    i18n
                    size="block"
                    type="submit"
                    [disabled]="totpGroup.invalid"
                    [submitting]="isSubmitting"
                >
                    Update MFA Enrollment
                </lsq-button>
            </div>
        </form>
    `,
    styleUrls: ['./edit-totp-modal.component.scss']
})
export class EditTotpModalComponent {
    public modalLoading: boolean;
    public readonly icons = Icons;
    public readonly colors = Color;
    public totpGroup = this._formBuilder.group({
        totpCode: [
            '',
            [
                Validators.required,
                LsqValidators.number,
                LsqValidators.exactLength(6),
                // This lets us show a custom error message on the input.
                () =>
                    this._mfaCodeFailure
                        ? { mfaCodeInvalid: { message: this._invalidAuthCodeCopy } }
                        : null
            ]
        ]
    });

    private _mfaCodeFailure = false;
    public qrCode: string;

    private readonly _invalidAuthCodeCopy = this._i18n({
        value: 'Invalid Code'
    });

    public isSubmitting = false;

    constructor(
        private _formBuilder: FormBuilder,
        private _i18n: I18n,
        private _createTotpMfaTokenService: CreateTotpMfaTokenService,
        private _verifyTotpMfaTokenService: VerifyTotpMfaTokenService,
        public modalWorkflowService: ModalWorkflowService,
        public authenticationService: AuthenticationService,
        public twoFactorService: TwoFactorService
    ) {
        this.modalLoading = true;
        void this.generateTotpCode();

        this.totpGroup.valueChanges.pipe(first()).subscribe(() => {
            this._mfaCodeFailure = false;
        });
    }

    public async generateTotpCode() {
        const createTotpResponse = await this._createTotpMfaTokenService.executeOnce(
            new CreateTotpMfaTokenRequest()
        );
        const secretCode = createTotpResponse.totpToken;
        const username = `FastTrack`;
        const issuer = `LSQ`;
        this.qrCode = `otpauth://totp/AWSCognito:${username}?secret=${secretCode}&issuer=${issuer}`;
        this.modalLoading = false;
    }

    public async submitTOTPCode(): Promise<void> {
        this._mfaCodeFailure = false;
        this.totpGroup.markAllAsTouched();
        this.totpGroup.get('totpCode').updateValueAndValidity();

        if (this.totpGroup.valid) {
            try {
                this.isSubmitting = true;

                const totpCode = this.totpGroup.get('totpCode').value;

                const response = await this._verifyTotpMfaTokenService.executeOnce(
                    new VerifyTotpMfaTokenRequest({ userCode: totpCode })
                );

                if (response.code !== ResponseCode.Success) {
                    throw new Error();
                } else {
                    this.modalWorkflowService.nextStep();
                    this.isSubmitting = false;
                }
            } catch (error) {
                this._mfaCodeFailure = true;
                this.totpGroup.get('totpCode').updateValueAndValidity();
                focusOnFirstInvalidLsqInput();
            }
        }
        this.isSubmitting = false;
        await this.authenticationService.setUserPreferredMfa('SOFTWARE_TOKEN_MFA');
        this.twoFactorService.preferredMfaMethodSubject.next('SOFTWARE_TOKEN_MFA');
    }
}
